import React, { useState } from "react";
import "./login.scss";
import logo from "../../assets/images/townOf1000Trails.svg";
import { Card, CardBody, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import HookForm from "../../components/hookForm/hookForm";
import TextField from "../../components/textField/textField";
import Constant from "../../util/constant";
import CustomButton from "../../components/customButton/customButton";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/features/toast/toastSlice";
import axios from "axios";
import { LOGIN } from "../../api/endPoint";
import { Helmet } from "react-helmet";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginForm = {
    email: {
      name: "email",
      validate: {
        required: {
          value: true,
        },
        pattern: {
          value: Constant.REGEX.EMAIL,
          message: t("PLEASE_ENTER_A_VALID_EMAIL_ADDRESS"),
        },
        validate: {
          noConsecutiveDots: (value: any) => {
            if (value.includes("..")) {
              return t("EMAIL_CANNOT_CONTAIN_CONSECUTIVE_DOTS");
            }
            const domainPart = value.split("@")[1];
            if (domainPart && domainPart.includes("..")) {
              return t("EMAIL_CANNOT_HAVE_CONSECUTIVE_DOTS_IN_DOMAIN");
            }
            return true;
          },
        },
      },
    },
    password: {
      name: "password",
      validate: {
        required: {
          value: true,
        },
        pattern: {
          value: Constant.REGEX.PASSWORD,
          message: t("PASSWORD_IS_NOT_VALID"),
        },
      },
    },
  };

  const onLogin = async (formData: any) => {
    setLoading(true);
    try {
      console.log(process.env.REACT_APP_API_BASE_URL);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${LOGIN}`,
        {
          email: formData.email,
          password: formData.password,
        }
      );
      console.log(response);
      const { accessToken, refreshToken } = response.data.data;

      // Store tokens in local storage
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      // Dispatch success toast
      dispatch(
        showToast({
          show: true,
          message: "Login successful",
          type: "success",
        })
      );

      // Navigate to the protected route
      navigate("/trails");
    } catch (error: any) {
      // Handle error
      dispatch(
        showToast({
          show: true,
          message:
            error.response?.data?.message ||
            "Unable to login. Please try again.   ",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("signInEmail", value); // Save email as a plain string
    }
  };
  const handlePasswordChange = (value: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("signInPassword", value); // Save email as a plain string
    }
  };
  const getInitialFormData = () => ({
    email: localStorage.getItem("signInEmail") || "",
    password: localStorage.getItem("signInPassword") || "",
  });

  return (
    <>
      <Helmet>
        <title>Town of 1000 Trails Admin Panel</title>
        <meta
          name="description"
          content="Welcome to the Town of 1000 Trails Admin Panel. Log in to manage in-app trails and more."
        />
      </Helmet>
      <div className="login-container">
        <div>
          <img className="pt-50 pb-30 logo" src={logo} alt="logo" />
          <Card className="overflow-hidden cardStyle" color="white">
            <CardBody className="d-flex  bg-indigo justify-content-between p-24 ">
              <div className="d-flex flex-column">
                <h4 className="text-left text-primary">{t("LOGIN")}</h4>
                <span className="title text-primary">
                  {t("PLEASE_ENTER_YOUR_CREDENTIALS")}
                </span>
              </div>
              <div className="iconStyle">
                <i className="icon icon-box-arrow-in-right"></i>
              </div>
            </CardBody>
            <CardBody className="text-center px-24 py-32">
              <HookForm
                defaultForm={getInitialFormData()}
                onSubmit={async (formData) => {
                  onLogin(formData);
                }}
              >
                {(formMethod) => {
                  return (
                    <>
                      <div className="loginWrapper mb-5">
                        <div className="texFeildWrapper mb-3">
                          <div className="d-flex">
                            <Label className="lable">
                              {t("EMAIL_ADDRESS")}
                            </Label>{" "}
                            <span className="redStar mx-2">*</span>
                          </div>
                          <TextField
                            formMethod={formMethod}
                            rules={loginForm.email.validate}
                            name={loginForm.email.name}
                            errors={formMethod?.formState.errors}
                            autoFocus={true}
                            required={true}
                            disabled={loading}
                            maxLength={256}
                            type="text"
                            inputMode="email"
                            placeholder={t("ENTER_EMAIL_ADDRESS")}
                            value={formMethod.getValues(loginForm.email.name)}
                            onChange={(e) => {
                              const trimmedValue = e.target.value
                                .replace(/\s/g, "")
                                .toLowerCase();
                              formMethod.setValue(
                                loginForm.email.name,
                                trimmedValue
                              ); // Update and validate
                              handleEmailChange(trimmedValue);
                            }}
                          />
                        </div>
                        <div className="texFeildWrapper mb-3">
                          <div className="d-flex">
                            <Label className="lable">{t("PASSWORD")}</Label>{" "}
                            <span className="redStar mx-2">*</span>
                          </div>
                          <div className="passWrapper">
                            <TextField
                              formMethod={formMethod}
                              rules={loginForm.password.validate}
                              name={loginForm.password.name}
                              errors={formMethod?.formState.errors}
                              autoFocus={false}
                              required={true}
                              disabled={loading}
                              passwordType={true}
                              type={showPass ? "text" : "password"}
                              placeholder={t("ENTER_PASSWORD")}
                              onChange={(e) => {
                                const trimmedValue = e.target.value.trim(); // Trim spaces
                                formMethod.setValue(
                                  loginForm.password.name,
                                  trimmedValue
                                ); // Update form state with trimmed value
                                handlePasswordChange(trimmedValue); // Optionally call your handler to manage the state if needed
                              }}
                            />
                            <i
                              onClick={() => setShowPass(!showPass)}
                              className={
                                showPass
                                  ? "icon icon-eye-fill"
                                  : "icon icon-eye-slash-fill"
                              }
                            ></i>
                          </div>
                        </div>
                      </div>
                      <div className="forgotPassLink text-end">
                        <Link to="/forgot-password" className="text-primary">
                          {t("FORGOT_PASSWORD")}
                        </Link>
                      </div>
                      <div className="submitDiv">
                        <CustomButton
                          className="btn btn-danger text-white btn-lg w-100 text-center justify-content-center"
                          type="submit"
                          title={t("LOGIN")}
                          loading={loading}
                          disabled={!formMethod?.formState.isValid}
                        />
                      </div>
                    </>
                  );
                }}
              </HookForm>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Login;
